/**
 * NavigationManager - Manages navigation hotspots and transitions
 */

import * as THREE from 'three';

export class NavigationManager {
  constructor(options) {
    const defaults = {
      scene: null,
      camera: null,
      config: null,
      model: null,
      renderer: null,
      spatialCursor: null,
    };
    const navigationManagerOptions = { ...defaults, ...options };
    const { scene, camera, config, model, renderer, spatialCursor } = navigationManagerOptions;

    this.scene = scene;
    this.camera = camera;
    this.config = config;
    this.model = model; // Store model reference for position offset
    this.renderer = renderer;
    this.spatialCursor = spatialCursor;
    this.hotspots = [];
    this.currentPanoId = null;
    this.raycaster = new THREE.Raycaster();
    this.mouse = new THREE.Vector2();
    this.hoveredHotspot = null;
    
    this.onNavigateCallback = null;
    
    this.setupEventListeners();
  }

  setupEventListeners() {
    window.addEventListener('click', this.onClick.bind(this));
    window.addEventListener('mousemove', this.onMouseMove.bind(this));
  }

  /**
   * Create hotspots for current panorama
   */
  createHotspots(panoId) {
    // Clear existing hotspots
    this.clearHotspots();
    
    this.currentPanoId = panoId;
    const panoConfig = this.config.panoramas[panoId];
    
    if (!panoConfig || !panoConfig.connections) {
      console.warn(`No connections found for panorama ${panoId}`);
      return;
    }
    
    // Create hotspot for each connected panorama
    panoConfig.connections.forEach(targetId => {
      const targetConfig = this.config.panoramas[targetId];
      if (!targetConfig) return;
      
      // Calculate direction to target (apply model offset to match actual camera positions)
      const modelOffset = this.model ? this.model.position : { x: 0, y: 0, z: 0 };
      
      const currentPos = new THREE.Vector3(
        panoConfig.position.x + modelOffset.x,
        panoConfig.position.y + modelOffset.y,
        panoConfig.position.z + modelOffset.z
      );
      
      const targetPos = new THREE.Vector3(
        targetConfig.position.x + modelOffset.x,
        targetConfig.position.y + modelOffset.y,
        targetConfig.position.z + modelOffset.z
      );
      
      console.log(`  ✓ Hotspot ${targetId}: Current (${currentPos.x.toFixed(2)}, ${currentPos.y.toFixed(2)}, ${currentPos.z.toFixed(2)}) → Target (${targetPos.x.toFixed(2)}, ${targetPos.y.toFixed(2)}, ${targetPos.z.toFixed(2)})`);
      
      // Create hotspot at the target's world position
      const hotspot = this.createHotspot(targetId, targetPos, targetConfig.name);
      this.hotspots.push(hotspot);
      // Add to scene (NOT camera - we want world-space positioning)
      this.scene.add(hotspot);
      console.log(`  ✓ Added hotspot for pano ${targetId} to scene`);
    });
    
    console.log(`✅ Created ${this.hotspots.length} navigation hotspots for panorama ${panoId}`);
    console.log(`📷 Camera now has ${this.camera.children.length} children`);
  }

  /**
   * Create a single hotspot mesh
   */
  createHotspot(targetId, targetWorldPosition, label) {
    const group = new THREE.Group();
    group.name = `hotspot_${targetId}`;
    
    // Position hotspot at the target's world position
    // Place it slightly above the floor to avoid z-fighting and ensure it passes depth test
    group.position.set(
      targetWorldPosition.x,
      0.05, // Slightly above floor level to pass depth test
      targetWorldPosition.z
    );
    
    console.log(`    📍 Hotspot ${targetId} positioned at world (${group.position.x.toFixed(2)}, ${group.position.y.toFixed(2)}, ${group.position.z.toFixed(2)})`);
    
    // Create simple white ring puck (like image reference, 20% smaller)
    const ringGeometry = new THREE.RingGeometry(0.096, 0.144, 32); // Inner radius 0.096, outer radius 0.144 (20% smaller)
    const ringMaterial = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.45, // More transparent
      side: THREE.DoubleSide,
      depthTest: true, // Enable depth testing so pucks are hidden behind geometry
      depthWrite: false,
      depthFunc: THREE.LessEqualDepth // Render if depth is less than or equal (allows rendering at same depth)
    });
    const circle = new THREE.Mesh(ringGeometry, ringMaterial);
    circle.rotation.x = -Math.PI / 2; // Lay flat on floor
    group.add(circle);
    
    // Add invisible center circle to make the hollow part clickable
    const centerGeometry = new THREE.CircleGeometry(0.096, 32); // Same size as inner ring radius
    const centerMaterial = new THREE.MeshBasicMaterial({
      transparent: true,
      opacity: 0, // Completely invisible
      side: THREE.DoubleSide,
      depthTest: true,
      depthWrite: false
    });
    const centerCircle = new THREE.Mesh(centerGeometry, centerMaterial);
    centerCircle.rotation.x = -Math.PI / 2; // Lay flat on floor
    group.add(centerCircle);
    
    // Store metadata and original position
    group.userData = {
      type: 'navigationHotspot',
      targetPanoId: targetId,
      label: label,
      originalOpacity: 0.45, // More transparent
      circle: circle,
      baseScale: 1.0, // Fixed scale - no hover scaling
      isHovered: false,
      originalPosition: group.position.clone() // Store original position to prevent drift
    };
    
    // Add pulsing animation
    this.animateHotspot(group);
    
    return group;
  }

  /**
   * Animate hotspot (disabled - no pulsing)
   */
  animateHotspot(hotspot) {
    // Animation disabled - pucks remain static
    hotspot.userData.animate = null;
  }

  /**
   * Update hotspot animations, distance-based scaling, and billboard rotation
   */
  update() {
    this.hotspots.forEach(hotspot => {
      // Restore original position to prevent drift during navigation
      if (hotspot.userData.originalPosition) {
        hotspot.position.copy(hotspot.userData.originalPosition);
      }
      
      // Animate pulsing
      if (hotspot.userData.animate) {
        hotspot.userData.animate();
      }
      
      // Distance-based scaling for depth perception
      const distance = hotspot.position.distanceTo(this.camera.position);
      
      // Calculate scale based on distance
      // Base scale is 1.0 at 2 units away
      // Scale decreases as distance increases
      const minScale = 0.4; // Don't go smaller than 40% of original size
      const maxScale = 1.2; // Don't go larger than 120% at very close range
      const referenceDistance = 2.0; // Distance where scale is 1.0
      
      // Linear scaling: closer = bigger, further = smaller
      let distanceScale = referenceDistance / Math.max(distance, 0.5);
      distanceScale = Math.max(minScale, Math.min(maxScale, distanceScale));
      
      // Apply ONLY distance-based scaling (no hover scaling)
      const finalScale = distanceScale;
      
      hotspot.scale.set(finalScale, finalScale, finalScale);
      
      // Billboard effect: Make puck face the camera
      if (hotspot.userData.circle) {
        // Get direction from puck to camera
        const direction = new THREE.Vector3();
        direction.subVectors(this.camera.position, hotspot.position).normalize();
        
        // Calculate rotation to face camera
        const angle = Math.atan2(direction.x, direction.z);
        
        // Apply rotation to the ring
        hotspot.userData.circle.rotation.x = -Math.PI / 2; // Keep flat on floor
        hotspot.userData.circle.rotation.z = -angle; // Rotate to face camera
      }
    });
  }

  onMouseMove(event) {
    // Update mouse position
    this.mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
    this.mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;
    
    // Raycast to check for hotspot hover
    this.raycaster.setFromCamera(this.mouse, this.camera);
    const intersects = this.raycaster.intersectObjects(this.hotspots, true);
    
    // Reset previously hovered hotspot
    if (this.hoveredHotspot) {
      this.unhighlightHotspot(this.hoveredHotspot);
      this.hoveredHotspot = null;
      this.renderer.domElement.dataset.state = 'grab';
    }
    
    // Highlight new hotspot
    if (intersects.length > 0) {
      let hotspot = intersects[0].object;
      
      // Find parent hotspot group
      while (hotspot && !hotspot.userData.type) {
        hotspot = hotspot.parent;
      }
      
      if (hotspot && hotspot.userData.type === 'navigationHotspot') {
        this.hoveredHotspot = hotspot;
        this.highlightHotspot(hotspot);
        this.renderer.domElement.dataset.state = 'pointer';
        this.spatialCursor.setEnabled(false);
        
        // Show tooltip
        this.showTooltip(hotspot.userData.label, event.clientX, event.clientY);
      }
    } else {
      this.hideTooltip();
      this.spatialCursor.setEnabled(true);
    }
  }

  onClick(event) {
    if (!this.hoveredHotspot) return;
    
    const targetId = this.hoveredHotspot.userData.targetPanoId;
    console.log(`🎯 Clicked navigation hotspot to panorama ${targetId}`);
    
    // Trigger navigation callback
    if (this.onNavigateCallback) {
      this.onNavigateCallback(targetId);
    }
  }

  highlightHotspot(hotspot) {
    if (!hotspot.userData.circle) return;
    
    // NO scale change on hover - keep size constant
    hotspot.userData.isHovered = true;
    
    // Brighten on hover (but not fully opaque)
    hotspot.userData.circle.material.opacity = 0.75; // Brighter on hover
  }

  unhighlightHotspot(hotspot) {
    if (!hotspot.userData.circle) return;
    
    // NO scale change needed
    hotspot.userData.isHovered = false;
    
    // Restore normal opacity
    hotspot.userData.circle.material.opacity = hotspot.userData.originalOpacity;
  }

  showTooltip(text, x, y) {
    const tooltip = document.getElementById('tooltip');
    if (tooltip) {
      tooltip.textContent = text;
      tooltip.style.display = 'block';
      tooltip.style.left = x + 'px';
      tooltip.style.top = y + 'px';
    }
  }

  hideTooltip() {
    const tooltip = document.getElementById('tooltip');
    if (tooltip) {
      tooltip.style.display = 'none';
    }
  }

  /**
   * Clear all hotspots
   */
  clearHotspots() {
    console.log(`🧹 Clearing ${this.hotspots.length} navigation hotspots`);
    
    this.hotspots.forEach((hotspot, index) => {
      // Make invisible first
      hotspot.visible = false;
      
      // Remove from scene (they're in world space)
      this.scene.remove(hotspot);
      console.log(`  ✓ Removed hotspot ${index} from scene`);
      
      // Dispose geometries and materials
      hotspot.traverse((child) => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) child.material.dispose();
      });
    });
    
    this.hotspots = [];
    this.hoveredHotspot = null;
    
    console.log(`✅ Navigation hotspots cleared`);
  }

  /**
   * Set navigation callback
   */
  onNavigate(callback) {
    this.onNavigateCallback = callback;
  }

  /**
   * Clean up
   */
  dispose() {
    this.clearHotspots();
    window.removeEventListener('click', this.onClick);
    window.removeEventListener('mousemove', this.onMouseMove);
  }
}

